import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version — risk-awareness
export const riskAwarenessMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-ra-1',
    text: {
      en: 'What is the main purpose of keeping a safe following distance?',
      nl: 'Wat is het belangrijkste doel van een veilige volgafstand?',
      ar: 'ما الغرض الأساسي من الحفاظ على مسافة أمان كافية خلف المركبة التي أمامك؟',
    },
    options: [
      {
        id: 'q-ra-1o1',
        text: {
          en: 'To save fuel',
          nl: 'Om brandstof te besparen',
          ar: 'لتوفير الوقود',
        },
      },
      {
        id: 'q-ra-1o2',
        text: {
          en: 'To have time and space to stop or avoid a hazard',
          nl: 'Om tijd en ruimte te hebben om te stoppen of een gevaar te vermijden',
          ar: 'لكي يكون لديك الوقت والمساحة للتوقف أو تجنب خطر',
        },
      },
      {
        id: 'q-ra-1o3',
        text: {
          en: 'To allow others to overtake',
          nl: 'Om anderen inhalen te laten',
          ar: 'للسماح للآخرين بالتجاوز',
        },
      },
    ],
    correctAnswerId: 'q-ra-1o2',
    explanation: {
      en: 'A safe following distance gives you time to react and stop or avoid a hazard if the vehicle in front brakes suddenly.',
      nl: 'Een veilige volgafstand geeft u tijd om te reageren en te stoppen of een gevaar te vermijden als het voertuig voor u plots remt.',
      ar: 'مسافة الأمان الكافية تمنحك وقتا للتفاعل والتوقف أو تجنب خطر إذا فرملت المركبة أمامك فجأة.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-2',
    text: {
      en: 'You are approaching a blind bend. What should you do?',
      nl: 'U nadert een bocht zonder zicht. Wat moet u doen?',
      ar: 'أنت تقترب من منعطف لا ترى ما وراءه. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-ra-2o1',
        text: {
          en: 'Maintain speed; you have right of way',
          nl: 'Snelheid aanhouden; u heeft voorrang',
          ar: 'حافظ على السرعة؛ لك الأولوية',
        },
      },
      {
        id: 'q-ra-2o2',
        text: {
          en: 'Slow down and be prepared for hazards you cannot see',
          nl: 'Vertragen en voorbereid zijn op gevaren die u niet kunt zien',
          ar: 'أبطئ وكن مستعدا لمخاطر لا تراها',
        },
      },
      {
        id: 'q-ra-2o3',
        text: {
          en: 'Sound your horn and maintain speed',
          nl: 'Claxonneren en de snelheid aanhouden',
          ar: 'استخدم البوق واحتفظ بالسرعة',
        },
      },
    ],
    correctAnswerId: 'q-ra-2o2',
    explanation: {
      en: 'On a blind bend you cannot see what is ahead. Slow down and be prepared to stop or avoid obstacles, pedestrians, or oncoming traffic.',
      nl: 'In een bocht zonder zicht ziet u niet wat er komt. Vertraag en wees bereid te stoppen of obstakels, voetgangers of tegemoetkomend verkeer te vermijden.',
      ar: 'في منعطف بلا رؤية لا ترى ما أمامك. أبطئ وكن مستعدا للتوقف أو تجنب عوائق أو مشاة أو مرور قادم من الاتجاه المعاكس.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-3',
    text: {
      en: 'Why should you check your blind spot before changing lanes?',
      nl: 'Waarom moet u uw dode hoek controleren voordat u van rijstrook wisselt?',
      ar: 'لماذا يجب أن تتحقق من النقطة العمياء قبل تغيير المسار؟',
    },
    options: [
      {
        id: 'q-ra-3o1',
        text: {
          en: 'It is required by law only on motorways',
          nl: 'Het is wettelijk alleen verplicht op autosnelwegen',
          ar: 'القانون يفرض ذلك على الطرق السريعة فقط',
        },
      },
      {
        id: 'q-ra-3o2',
        text: {
          en: 'Another vehicle or cyclist may be there and not visible in the mirror',
          nl: 'Daar kan een ander voertuig of fietser staan die in de spiegel niet zichtbaar is',
          ar: 'قد تكون هناك مركبة أو راكب دراجة لا يظهر في المرآة',
        },
      },
      {
        id: 'q-ra-3o3',
        text: {
          en: 'To reduce fuel consumption',
          nl: 'Om het brandstofverbruik te verlagen',
          ar: 'لتقليل استهلاك الوقود',
        },
      },
    ],
    correctAnswerId: 'q-ra-3o2',
    explanation: {
      en: 'The blind spot is an area not covered by mirrors. Checking it before changing lanes prevents collisions with vehicles or cyclists beside you.',
      nl: 'De dode hoek is een gebied dat de spiegels niet dekken. Controleren vóór het wisselen van strook voorkomt aanrijdingen met voertuigen of fietsers naast u.',
      ar: 'النقطة العمياء منطقة لا تغطيها المرايا. التحقق منها قبل تغيير المسار يمنع الاصطدام بمركبات أو راكبي دراجات بجانبك.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-4',
    text: {
      en: 'What is "defensive driving"?',
      nl: 'Wat is defensief rijden?',
      ar: 'ما المقصود ب«القيادة الدفاعية»؟',
    },
    options: [
      {
        id: 'q-ra-4o1',
        text: {
          en: 'Driving slowly at all times',
          nl: 'Altijd langzaam rijden',
          ar: 'القيادة ببطء في كل الأوقات',
        },
      },
      {
        id: 'q-ra-4o2',
        text: {
          en: 'Anticipating hazards and acting in good time to avoid danger',
          nl: 'Gevaren voorzien en tijdig handelen om gevaar te vermijden',
          ar: 'استباق المخاطر والتصرف في الوقت المناسب لتجنب الخطر',
        },
      },
      {
        id: 'q-ra-4o3',
        text: {
          en: 'Letting others go first always',
          nl: 'Anderen altijd voor laten gaan',
          ar: 'إعطاء الأولوية للآخرين دائما',
        },
      },
    ],
    correctAnswerId: 'q-ra-4o2',
    explanation: {
      en: 'Defensive driving means anticipating what might happen and adjusting your speed and position to avoid danger before it occurs.',
      nl: 'Defensief rijden betekent vooruitdenken en uw snelheid en positie aanpassen om gevaar te vermijden voordat het zich voordoet.',
      ar: 'القيادة الدفاعية تعني توقع ما قد يحدث وتعديل السرعة والموضع لتجنب الخطر قبل وقوعه.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-5',
    text: {
      en: 'You see children playing near the road. What should you do?',
      nl: 'U ziet kinderen spelen bij de weg. Wat moet u doen?',
      ar: 'ترى أطفالا يلعبون قرب الطريق. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-ra-5o1',
        text: {
          en: 'Continue at the same speed',
          nl: 'Met dezelfde snelheid verder rijden',
          ar: 'تابع بنفس السرعة',
        },
      },
      {
        id: 'q-ra-5o2',
        text: {
          en: 'Slow down and be prepared to stop; they may run into the road',
          nl: 'Vertragen en klaar zijn om te stoppen; zij kunnen de weg op rennen',
          ar: 'أبطئ وكن مستعدا للتوقف؛ قد يركضون إلى الطريق',
        },
      },
      {
        id: 'q-ra-5o3',
        text: {
          en: 'Sound your horn to warn them',
          nl: 'Claxonneren om hen te waarschuwen',
          ar: 'استخدم البوق لتحذيرهم',
        },
      },
    ],
    correctAnswerId: 'q-ra-5o2',
    explanation: {
      en: 'Children near the road are unpredictable. Slow down and be ready to stop; they may run onto the road without looking.',
      nl: 'Kinderen bij de weg zijn onvoorspelbaar. Vertraag en wees klaar om te stoppen; zij kunnen zonder te kijken de weg op rennen.',
      ar: 'الأطفال قرب الطريق غير متوقعين. أبطئ وكن مستعدا للتوقف؛ قد يركضون إلى الطريق دون أن ينظروا.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-6',
    text: {
      en: 'What is a "safe stopping distance" made up of?',
      nl: 'Waaruit bestaat een veilige remafstand?',
      ar: 'مم يتكون «مسافة التوقف الآمنة»؟',
    },
    options: [
      {
        id: 'q-ra-6o1',
        text: {
          en: 'Only the distance your car travels while you brake',
          nl: 'Alleen de afstand die uw auto aflegt tijdens het remmen',
          ar: 'المسافة التي تقطعها سيارتك أثناء الكبح فقط',
        },
      },
      {
        id: 'q-ra-6o2',
        text: {
          en: 'Thinking distance plus braking distance',
          nl: 'Reactieafstand plus remafstand',
          ar: 'مسافة التفاعل زائد مسافة الكبح',
        },
      },
      {
        id: 'q-ra-6o3',
        text: {
          en: 'Only the distance you can see ahead',
          nl: 'Alleen de afstand die u vooruit kunt zien',
          ar: 'المسافة التي تراها أمامك فقط',
        },
      },
    ],
    correctAnswerId: 'q-ra-6o2',
    explanation: {
      en: 'Stopping distance = thinking distance (while you react) + braking distance (while the car stops). Both increase with speed.',
      nl: 'Remafstand = reactieafstand (tijdens uw reactie) + remafstand (terwijl de auto stopt). Beide nemen toe met de snelheid.',
      ar: 'مسافة التوقف = مسافة التفاعل (أثناء رد فعلك) + مسافة الكبح (حتى تتوقف السيارة). كلاهما يزداد مع السرعة.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-7',
    text: {
      en: 'You are following a large lorry. Why should you keep a greater distance than usual?',
      nl: 'U rijdt achter een grote vrachtwagen. Waarom moet u een grotere afstand aanhouden dan gewoonlijk?',
      ar: 'أنت تتبع شاحنة كبيرة. لماذا يجب أن تترك مسافة أكبر من المعتاد؟',
    },
    options: [
      {
        id: 'q-ra-7o1',
        text: {
          en: 'To save fuel by slipstreaming',
          nl: 'Om brandstof te besparen door in de luwte te rijden',
          ar: 'لتوفير الوقود بالسير في المسار الهوائي خلفها',
        },
      },
      {
        id: 'q-ra-7o2',
        text: {
          en: 'You cannot see the road ahead; if it brakes hard you need more space to stop',
          nl: 'U ziet de weg voor u niet; bij hard remmen heeft u meer ruimte nodig om te stoppen',
          ar: 'لا ترى الطريق أمامك؛ إذا فرملت بقوة تحتاج مساحة أكبر للتوقف',
        },
      },
      {
        id: 'q-ra-7o3',
        text: {
          en: 'Lorries always brake suddenly',
          nl: 'Vrachtwagens remmen altijd plots',
          ar: 'الشاحنات تفرمل فجأة دائما',
        },
      },
    ],
    correctAnswerId: 'q-ra-7o2',
    explanation: {
      en: 'A large vehicle blocks your view of the road ahead. You need extra distance to see hazards and to stop safely if the lorry brakes.',
      nl: 'Een groot voertuig belemmer uw zicht op de weg voor u. U heeft extra afstand nodig om gevaren te zien en veilig te stoppen als de vrachtwagen remt.',
      ar: 'المركبة الكبيرة تحجب رؤيتك للطريق أمامك. تحتاج مسافة إضافية لرؤية المخاطر وللتوقف بأمان إذا فرملت الشاحنة.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-8',
    text: {
      en: 'What should you do when you see a vehicle with hazard lights on stopped on the hard shoulder?',
      nl: 'Wat moet u doen als u een voertuig met alarmlichten ziet stilstaan op de vluchtstrook?',
      ar: 'ماذا تفعل عندما ترى مركبة أضواء التحذير فيها مضاءة وهي متوقفة على كتف الطريق؟',
    },
    options: [
      {
        id: 'q-ra-8o1',
        text: {
          en: 'Stop and offer help',
          nl: 'Stoppen en hulp aanbieden',
          ar: 'توقف وعرض المساعدة',
        },
      },
      {
        id: 'q-ra-8o2',
        text: {
          en: 'Slow down and move away from the hard shoulder if safe; be ready for people or debris',
          nl: 'Vertragen en indien veilig uitwijken van de vluchtstrook; wees alert op personen of brokstukken',
          ar: 'أبطئ وابتعد عن كتف الطريق إن أمكن؛ كن مستعدا لأشخاص أو حطام',
        },
      },
      {
        id: 'q-ra-8o3',
        text: {
          en: 'Ignore it; it is not your concern',
          nl: 'Negeren; het gaat u niet aan',
          ar: 'تجاهل الأمر؛ لا يعنيك',
        },
      },
    ],
    correctAnswerId: 'q-ra-8o2',
    explanation: {
      en: 'Pass with care. People may be on the carriageway; move away from the hard shoulder if safe and reduce speed.',
      nl: 'Rijd voorzichtig voorbij. Personen kunnen op de rijbaan zijn; wijk veilig uit van de vluchtstrook en verlaag de snelheid.',
      ar: 'تجاوز بحذر. قد يكون هناك أشخاص على الممر؛ ابتعد عن الكتف إن أمكن وخفض السرعة.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-9',
    text: {
      en: 'Why is it riskier to drive at the same speed in rain as in dry weather?',
      nl: 'Waarom is het riskanter om in de regen dezelfde snelheid te rijden als bij droog weer?',
      ar: 'لماذا يزداد الخطر إذا قدت بنفس السرعة في المطر كما في الجو الجاف؟',
    },
    options: [
      {
        id: 'q-ra-9o1',
        text: {
          en: 'Rain makes the engine work harder',
          nl: 'Regen laat de motor harder werken',
          ar: 'المطر يجعل المحرك يعمل بجهد أكبر',
        },
      },
      {
        id: 'q-ra-9o2',
        text: {
          en: 'Stopping distance increases; grip is reduced',
          nl: 'De remafstand neemt toe; de grip neemt af',
          ar: 'تزداد مسافة التوقف؛ ويقل الالتصاق',
        },
      },
      {
        id: 'q-ra-9o3',
        text: {
          en: 'Other drivers drive slower',
          nl: 'Andere bestuurders rijden langzamer',
          ar: 'السائقون الآخرون يقودون أبطأ',
        },
      },
    ],
    correctAnswerId: 'q-ra-9o2',
    explanation: {
      en: 'Wet roads reduce grip. Your stopping distance increases, so you need to slow down and leave a larger gap.',
      nl: 'Nat wegdek vermindert de grip. Uw remafstand neemt toe; vertraag daarom en houd grotere afstand.',
      ar: 'الطريق المبلل يقلل الالتصاق. تزداد مسافة التوقف، فينبغي الإبطاء وزيادة المسافة.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-10',
    text: {
      en: 'You are overtaking. When should you start to pull back into your lane?',
      nl: 'U haalt in. Wanneer moet u terug naar uw rijstrook?',
      ar: 'أنت تتجاوز. متى يجب أن تعود إلى مسارك؟',
    },
    options: [
      {
        id: 'q-ra-10o1',
        text: {
          en: 'As soon as you are past the vehicle',
          nl: 'Zodra u het voertuig voorbij bent',
          ar: 'ما إن تجاوزت المركبة',
        },
      },
      {
        id: 'q-ra-10o2',
        text: {
          en: 'When you can see the overtaken vehicle in your interior mirror',
          nl: 'Wanneer u het ingehaalde voertuig in uw binnenspiegel ziet',
          ar: 'عندما ترى المركبة التي تجاوزتها في المرآة الداخلية',
        },
      },
      {
        id: 'q-ra-10o3',
        text: {
          en: 'When the other driver flashes you',
          nl: 'Wanneer de andere bestuurder u licht geeft',
          ar: 'عندما يومض السائق الآخر لك',
        },
      },
    ],
    correctAnswerId: 'q-ra-10o2',
    explanation: {
      en: 'Pull back in only when you can see the overtaken vehicle in your interior mirror. This keeps a safe distance.',
      nl: 'Ga pas terug wanneer u het ingehaalde voertuig in uw binnenspiegel ziet. Zo houdt u een veilige afstand.',
      ar: 'لا تعد إلى مسارك إلا عندما ترى المركبة المجاوزة في المرآة الداخلية. هكذا تحافظ على مسافة آمنة.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-11',
    text: {
      en: 'What is the main risk when driving close behind another vehicle?',
      nl: 'Wat is het belangrijkste risico als u dicht achter een ander voertuig rijdt?',
      ar: 'ما الخطر الرئيسي عند القيادة قريبا خلف مركبة أخرى؟',
    },
    options: [
      {
        id: 'q-ra-11o1',
        text: {
          en: 'You use more fuel',
          nl: 'U verbruikt meer brandstof',
          ar: 'يزداد استهلاك الوقود',
        },
      },
      {
        id: 'q-ra-11o2',
        text: {
          en: 'If it brakes suddenly you may not have time to stop',
          nl: 'Als het plots remt, heeft u misschien geen tijd om te stoppen',
          ar: 'إذا فرمل فجأة قد لا يتسنى لك الوقت للتوقف',
        },
      },
      {
        id: 'q-ra-11o3',
        text: {
          en: 'Your engine overheats',
          nl: 'Uw motor oververhit',
          ar: 'المحرك يسخن زيادة',
        },
      },
    ],
    correctAnswerId: 'q-ra-11o2',
    explanation: {
      en: 'Tailgating leaves no time to react. If the vehicle in front brakes suddenly, you may collide with it.',
      nl: 'Dicht op elkaar rijden laat geen reactietijd. Als het voertuig voor u plots remt, kunt u er tegenaan rijden.',
      ar: 'الالتصاق بالخلف لا يترك وقتا للتفاعل. إذا فرملت المركبة أمامك فجأة قد تصطدم بها.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-12',
    text: {
      en: 'You see a cyclist ahead. There is a pothole in the road. What might the cyclist do?',
      nl: 'U ziet een fietser voor u. Er zit een kuil in de weg. Wat kan de fietser doen?',
      ar: 'ترى راكب دراجة أمامك. هناك حفرة في الطريق. ماذا قد يفعل راكب الدراجة؟',
    },
    options: [
      {
        id: 'q-ra-12o1',
        text: {
          en: 'Nothing; cyclists ignore potholes',
          nl: 'Niets; fietsers negeren kuilen',
          ar: 'لا شيء؛ راكبو الدراجات يتجاهلون الحفر',
        },
      },
      {
        id: 'q-ra-12o2',
        text: {
          en: 'They may swerve to avoid it; give them space',
          nl: 'Hij of zij kan uitwijken om die te vermijden; geef ruimte',
          ar: 'قد ينحرف لتفاديها؛ أعطه مساحة',
        },
      },
      {
        id: 'q-ra-12o3',
        text: {
          en: 'They will stop',
          nl: 'Hij of zij stopt',
          ar: 'سيتوقف',
        },
      },
    ],
    correctAnswerId: 'q-ra-12o2',
    explanation: {
      en: 'Cyclists may need to swerve to avoid potholes or debris. Anticipate this and give them enough space.',
      nl: 'Fietsers moeten soms uitwijken voor kuilen of brokstukken. Anticipeer hierop en geef voldoende ruimte.',
      ar: 'قد يحتاج راكبو الدراجات للانحراف لتفادي الحفر أو الحطام. توقع ذلك وأعطهم مساحة كافية.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-13',
    text: {
      en: 'Why should you slow down when passing a parked car with its door partly open?',
      nl: 'Waarom moet u vertragen als u langs een geparkeerde auto rijdt waarvan het portier half open staat?',
      ar: 'لماذا يجب أن تبطئ عندما تمر بسيارة متوقفة وبابها مفتوح جزئيا؟',
    },
    options: [
      {
        id: 'q-ra-13o1',
        text: {
          en: 'To avoid scratching the door',
          nl: 'Om het portier niet te beschadigen',
          ar: 'لتجنب خدش الباب',
        },
      },
      {
        id: 'q-ra-13o2',
        text: {
          en: 'The door may open further or someone may step out',
          nl: 'Het portier kan verder open gaan of iemand kan uitstappen',
          ar: 'قد ينفتح الباب أكثر أو يخرج شخص',
        },
      },
      {
        id: 'q-ra-13o3',
        text: {
          en: 'Parked cars always have the engine running',
          nl: 'Geparkeerde auto\'s hebben altijd de motor aan',
          ar: 'السيارات المتوقفة تعمل محركاتها دائما',
        },
      },
    ],
    correctAnswerId: 'q-ra-13o2',
    explanation: {
      en: 'A partly open door may open fully or a person may get out. Slow down and be prepared to stop.',
      nl: 'Een half open portier kan volledig open gaan of iemand kan uitstappen. Vertraag en wees bereid te stoppen.',
      ar: 'الباب المفتوح قليلا قد ينفتح بالكامل أو يخرج شخص. أبطئ وكن مستعدا للتوقف.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-14',
    text: {
      en: 'What should you do when you see an animal at the side of the road?',
      nl: 'Wat moet u doen als u een dier aan de zijkant van de weg ziet?',
      ar: 'ماذا تفعل عندما ترى حيوانا على جانب الطريق؟',
    },
    options: [
      {
        id: 'q-ra-14o1',
        text: {
          en: 'Speed up to pass quickly',
          nl: 'Versnellen om snel voorbij te rijden',
          ar: 'زد السرعة لتمر بسرعة',
        },
      },
      {
        id: 'q-ra-14o2',
        text: {
          en: 'Slow down and be prepared to stop; it may run into the road',
          nl: 'Vertragen en klaar zijn om te stoppen; het kan de weg op rennen',
          ar: 'أبطئ وكن مستعدا للتوقف؛ قد يركض إلى الطريق',
        },
      },
      {
        id: 'q-ra-14o3',
        text: {
          en: 'Sound your horn continuously',
          nl: 'Continu claxonneren',
          ar: 'استخدم البوق باستمرار',
        },
      },
    ],
    correctAnswerId: 'q-ra-14o2',
    explanation: {
      en: 'Animals can move suddenly into the road. Slow down and be ready to stop; do not startle them with the horn.',
      nl: 'Dieren kunnen plots de weg op komen. Vertraag en wees klaar om te stoppen; schrik ze niet met de claxon.',
      ar: 'قد تندفع الحيوانات فجأة إلى الطريق. أبطئ وكن مستعدا للتوقف؛ لا تفزعها بالبوق.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-15',
    text: {
      en: 'You are driving on a road with many junctions. What should you do?',
      nl: 'U rijdt op een weg met veel zijwegen. Wat moet u doen?',
      ar: 'أنت تقود على طريق فيه تقاطعات كثيرة. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-ra-15o1',
        text: {
          en: 'Maintain high speed to clear the area',
          nl: 'Hoge snelheid aanhouden om het gebied snel te verlaten',
          ar: 'حافظ على سرعة عالية لتجاوز المنطقة',
        },
      },
      {
        id: 'q-ra-15o2',
        text: {
          en: 'Be alert; vehicles or pedestrians may emerge from side roads',
          nl: 'Blijf alert; voertuigen of voetgangers kunnen van zijwegen komen',
          ar: 'كن يقظا؛ قد تظهر مركبات أو مشاة من الطرق الفرعية',
        },
      },
      {
        id: 'q-ra-15o3',
        text: {
          en: 'Use the horn at each junction',
          nl: 'Bij elke zijweg claxonneren',
          ar: 'استخدم البوق عند كل تقاطع',
        },
      },
    ],
    correctAnswerId: 'q-ra-15o2',
    explanation: {
      en: 'Junctions are high-risk. Watch for vehicles pulling out or pedestrians crossing; be ready to slow down or stop.',
      nl: 'Kruisingen en zijwegen zijn risicovol. Let op uitrijdend verkeer en overstekende voetgangers; wees bereid te vertragen of te stoppen.',
      ar: 'التقاطعات عالية الخطر. راقب المركبات المنطلقة أو المشاة العابرين؛ كن مستعدا للإبطاء أو التوقف.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-16',
    text: {
      en: 'What does "anticipation" mean in driving?',
      nl: 'Wat betekent anticipatie in het verkeer?',
      ar: 'ماذا تعني «المبادرة الاستباقية» في القيادة؟',
    },
    options: [
      {
        id: 'q-ra-16o1',
        text: {
          en: 'Guessing what will happen',
          nl: 'Gokken wat er gaat gebeuren',
          ar: 'تخمين ما سيحدث',
        },
      },
      {
        id: 'q-ra-16o2',
        text: {
          en: 'Reading the road and other road users to prepare for possible hazards',
          nl: 'De weg en andere weggebruikers lezen om u voor te bereiden op mogelijke gevaren',
          ar: 'قراءة الطريق وبقية مستخدميه للاستعداد لمخاطر محتملة',
        },
      },
      {
        id: 'q-ra-16o3',
        text: {
          en: 'Driving in the future',
          nl: 'In de toekomst rijden',
          ar: 'القيادة في المستقبل',
        },
      },
    ],
    correctAnswerId: 'q-ra-16o2',
    explanation: {
      en: 'Anticipation means observing the road and others, and adjusting your speed and position in case a hazard develops.',
      nl: 'Anticipatie betekent de weg en anderen observeren en uw snelheid en positie aanpassen voor het geval zich een gevaar voordoet.',
      ar: 'الاستباق يعني مراقبة الطريق والآخرين وتعديل السرعة والموضع إذا نشأ خطر.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-17',
    text: {
      en: 'Why is it dangerous to drive when you are tired?',
      nl: 'Waarom is het gevaarlijk om te rijden als u moe bent?',
      ar: 'لماذا يكون القيادة أثناء التعب خطيرة؟',
    },
    options: [
      {
        id: 'q-ra-17o1',
        text: {
          en: 'Your fuel consumption increases',
          nl: 'Uw brandstofverbruik neemt toe',
          ar: 'يزداد استهلاك الوقود',
        },
      },
      {
        id: 'q-ra-17o2',
        text: {
          en: 'Your reaction time and concentration get worse; you may fall asleep',
          nl: 'Uw reactietijd en concentratie verslechteren; u kunt in slaap vallen',
          ar: 'تتدهور سرعة التفاعل والتركيز؛ وقد تغفو أثناء القيادة',
        },
      },
      {
        id: 'q-ra-17o3',
        text: {
          en: 'The car handles differently',
          nl: 'De auto stuurt anders',
          ar: 'تتصرف السيارة بشكل مختلف',
        },
      },
    ],
    correctAnswerId: 'q-ra-17o2',
    explanation: {
      en: 'Tiredness slows your reactions and reduces concentration. You may also fall asleep at the wheel. Stop and rest.',
      nl: 'Vermoeidheid vertraagt uw reacties en vermindert de concentratie. U kunt ook in slaap vallen achter het stuur. Stop en rust uit.',
      ar: 'التعب يبطئ رد الفعل ويقلل التركيز. وقد تغفو أثناء القيادة. توقف واسترح.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-18',
    text: {
      en: 'You are approaching a green light. The vehicle in front is slowing down. What might be the reason?',
      nl: 'U nadert een groen licht. Het voertuig voor u vertraagt. Wat kan de reden zijn?',
      ar: 'أنت تقترب من إشارة خضراء. المركبة أمامك تبطئ. ما السبب المحتمل؟',
    },
    options: [
      {
        id: 'q-ra-18o1',
        text: {
          en: 'The driver is lost',
          nl: 'De bestuurder is de weg kwijt',
          ar: 'السائق تائه',
        },
      },
      {
        id: 'q-ra-18o2',
        text: {
          en: 'The light may be about to change, or there may be a hazard; be prepared to stop',
          nl: 'Het licht kan bijna veranderen, of er is een gevaar; wees bereid te stoppen',
          ar: 'قد تتغير الإشارة قريبا، أو يوجد خطر؛ كن مستعدا للتوقف',
        },
      },
      {
        id: 'q-ra-18o3',
        text: {
          en: 'There is no reason',
          nl: 'Er is geen reden',
          ar: 'لا يوجد سبب',
        },
      },
    ],
    correctAnswerId: 'q-ra-18o2',
    explanation: {
      en: 'The driver may be preparing to stop for amber/red, or avoiding a hazard. Do not assume the light will stay green; be ready to stop.',
      nl: 'De bestuurder bereidt zich misschien voor om te stoppen voor oranje/rood, of ontwijkt een gevaar. Ga er niet van uit dat het licht groen blijft; wees klaar om te stoppen.',
      ar: 'قد يستعد السائق للتوقف عند البرتقالي/الأحمر، أو يتجنب خطرا. لا تفترض بقاء الإشارة خضراء؛ كن مستعدا للتوقف.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-19',
    text: {
      en: 'What should you do when you see a bus indicating to pull out from a bus stop?',
      nl: 'Wat moet u doen als u een bus ziet die aangeeft weg te willen rijden vanaf een bushalte?',
      ar: 'ماذا تفعل عندما ترى حافلة تشير إلى الاندماج من موقف؟',
    },
    options: [
      {
        id: 'q-ra-19o1',
        text: {
          en: 'Accelerate to pass before it moves',
          nl: 'Versnellen om voorbij te rijden voordat hij wegrijdt',
          ar: 'زد السرعة لتمر قبل أن تتحرك',
        },
      },
      {
        id: 'q-ra-19o2',
        text: {
          en: 'Slow down and give way if it is safe; the bus may be joining the flow',
          nl: 'Vertragen en voorrang verlenen indien veilig; de bus voegt mogelijk in',
          ar: 'أبطئ وامنح الأولوية إن أمكن؛ قد تنضم الحافلة إلى تيار المرور',
        },
      },
      {
        id: 'q-ra-19o3',
        text: {
          en: 'Sound your horn',
          nl: 'Claxonneren',
          ar: 'استخدم البوق',
        },
      },
    ],
    correctAnswerId: 'q-ra-19o2',
    explanation: {
      en: 'In many areas you should give way to buses pulling out. Slow down and let it in when safe.',
      nl: 'In veel gebieden moet u bussen die wegrijden van een halte voorrang verlenen. Vertraag en laat de bus invoegen als het veilig is.',
      ar: 'في كثير من الأماكن يجب منح الأولوية للحافلات المنطلقة. أبطئ واسمح لها بالاندماج عندما يكون ذلك آمنا.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-20',
    text: {
      en: 'Why should you avoid driving in the "blind spot" of a lorry?',
      nl: 'Waarom moet u vermijden in de dode hoek van een vrachtwagen te rijden?',
      ar: 'لماذا يجب تجنب القيادة في «النقطة العمياء» للشاحنة؟',
    },
    options: [
      {
        id: 'q-ra-20o1',
        text: {
          en: 'To save fuel',
          nl: 'Om brandstof te besparen',
          ar: 'لتوفير الوقود',
        },
      },
      {
        id: 'q-ra-20o2',
        text: {
          en: 'The lorry driver may not see you and could change lane or turn',
          nl: 'De vrachtwagenchauffeur ziet u misschien niet en kan van strook wisselen of afslaan',
          ar: 'قد لا يراك سائق الشاحنة فيغير المسار أو ينعطف',
        },
      },
      {
        id: 'q-ra-20o3',
        text: {
          en: 'Lorries are slower',
          nl: 'Vrachtwagens zijn langzamer',
          ar: 'الشاحنات أبطأ',
        },
      },
    ],
    correctAnswerId: 'q-ra-20o2',
    explanation: {
      en: 'Lorries have large blind spots. If you stay in them, the driver may not see you and could move into your lane or turn across you.',
      nl: 'Vrachtwagens hebben grote dode hoeken. Blijft u daarin, dan ziet de chauffeur u mogelijk niet en kan naar uw strook bewegen of voor u afslaan.',
      ar: 'للشاحنات نقاط عمياء واسعة. إذا بقيت فيها قد لا يراك السائق فيتحرك إلى مسارك أو ينعطف أمامك.',
    },
    subject: 'Risk Awareness',
  },
  {
    id: 'q-ra-21',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/risk-awareness/q-ra-21-corner-visibility.jpg',
    text: {
      en: 'When parking at a corner, what safety risk must you avoid?',
      nl: 'Wat is het veiligheidsrisico dat u moet vermijden wanneer u op een hoek parkeert?',
      ar: 'ما خطر السلامة الذي يجب عليك تجنبه عند الوقوف في الزاوية؟'
    },
    options: [
      {
        id: 'q-ra-21o1',
        text: {
          en: 'Blocking the view of road signs',
          nl: 'Het blokkeren van het zicht op verkeersborden',
          ar: 'حجب رؤية علامات الطريق'
        }
      },
      {
        id: 'q-ra-21o2',
        text: {
          en: 'Reducing visibility for drivers; parked vehicles at corners create blind spots for turning traffic',
          nl: 'Zicht verminderen voor bestuurders; geparkeerde voertuigen op hoeken veroorzaken dode hoeken voor afsluitend verkeer',
          ar: 'تقليل الرؤية للسائقين؛ المركبات المتوقفة في الزوايا تخلق نقاط عمياء لحركة المرور المنعطفة'
        }
      },
      {
        id: 'q-ra-21o3',
        text: {
          en: 'Taking up too much parking space',
          nl: 'Te veel parkeerplaats innemen',
          ar: 'احتلال مساحة وقوف كثيرة جدا'
        }
      }
    ],
    correctAnswerId: 'q-ra-21o2',
    explanation: {
      en: 'Never park too close to corners. Parked vehicles obstruct visibility for drivers trying to turn, increasing accident risk. Maintain clear sightlines at intersections and corners. (RVV 1990 art. 12)',
      nl: 'Park nooit te dicht bij hoeken. Geparkeerde voertuigen blokkeren het zicht voor bestuurders die willen afslaan, waardoor het ongeluksrisico toeneemt. Houd helder zicht op kruisingen en hoeken. (RVV 1990 art. 12)',
      ar: 'لا تنقف أبدا بالقرب من الزوايا. المركبات المتوقفة تحجب الرؤية لسائقي السيارات الذين يحاولون الانعطاف مما يزيد من خطر الحوادث. حافظ على خطوط نظر واضحة عند التقاطعات والزوايا. (RVV 1990 art. 12)'
    },
    subject: 'Risk Awareness'
  },
  {
    id: 'q-ra-22',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/risk-awareness/q-ra-22-residential-zone.jpg',
    text: {
      en: 'What must you observe when parking in residential zones?',
      nl: 'Wat moet je opmerken wanneer je in woongebieden parkeert?',
      ar: 'ما الذي يجب عليك ملاحظته عند الوقوف في المناطق السكنية؟'
    },
    options: [
      {
        id: 'q-ra-22o1',
        text: {
          en: 'Park anywhere within the zone',
          nl: 'Park overal in de zone',
          ar: 'اوقف في أي مكان ضمن المنطقة'
        }
      },
      {
        id: 'q-ra-22o2',
        text: {
          en: 'Park only in marked parking spaces and follow all local parking signs and regulations',
          nl: 'Park alleen op aangewezen parkeerplaatsen en volg alle lokale parkeerregels en -voorschriften',
          ar: 'اوقف فقط في مواقف باركينج محددة واتبع جميع علامات ولوائح الوقوف المحلية'
        }
      },
      {
        id: 'q-ra-22o3',
        text: {
          en: 'Parking is always prohibited in residential zones',
          nl: 'Parkeren is altijd verboden in woongebieden',
          ar: 'الوقوف محظور دائما في المناطق السكنية'
        }
      }
    ],
    correctAnswerId: 'q-ra-22o2',
    explanation: {
      en: 'In residential zones, you must park only in designated parking spaces and follow all posted parking signs and local regulations. Improper parking can result in fines and vehicle removal. (RVV 1990 art. 12)',
      nl: 'In woongebieden mag je alleen op aangewezen parkeerplaatsen parkeren en moet je alle aangekondigde parkeerregels en lokale bepalingen volgen. Onjuist parkeren kan tot boetes en voertuigverwijdering leiden. (RVV 1990 art. 12)',
      ar: 'في المناطق السكنية، يجب عليك الوقوف فقط في مواقف محددة واتباع جميع علامات الوقوف المعلنة واللوائح المحلية. قد يؤدي الوقوف غير الصحيح إلى غرامات وإزالة المركبة. (RVV 1990 art. 12)'
    },
    subject: 'Risk Awareness'
  },
  {
    id: 'q-ra-23',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/risk-awareness/q-ra-23-tram-tracks.jpg',
    text: {
      en: 'What safety risk must you avoid when parking near tram tracks?',
      nl: 'Wat is het veiligheidsrisico dat u moet vermijden wanneer u dicht bij tramrails parkeert?',
      ar: 'ما خطر السلامة الذي يجب عليك تجنبه عند الوقوف بالقرب من مسارات الترام؟'
    },
    options: [
      {
        id: 'q-ra-23o1',
        text: {
          en: 'Blocking tram access and interfering with tram operation',
          nl: 'Tramtoegang blokkeren en de tramwerking verstoren',
          ar: 'حجب وصول الترام والتداخل مع تشغيل الترام'
        }
      },
      {
        id: 'q-ra-23o2',
        text: {
          en: 'Damaging the tram tracks',
          nl: 'De tramrails beschadigen',
          ar: 'إتلاف مسارات الترام'
        }
      },
      {
        id: 'q-ra-23o3',
        text: {
          en: 'There is no safety risk near tram tracks',
          nl: 'Er is geen veiligheidsrisico dicht bij tramrails',
          ar: 'لا يوجد خطر أمان بالقرب من مسارات الترام'
        }
      }
    ],
    correctAnswerId: 'q-ra-23o1',
    explanation: {
      en: 'Never park on or too close to tram tracks. Parked vehicles interfere with tram operation and create safety hazards for all road users. Parking violations near tram tracks can result in fines and vehicle removal. (RVV 1990 art. 12)',
      nl: 'Park nooit op of te dicht bij tramrails. Geparkeerde voertuigen verstoren de tramwerking en creëren veiligheidsrisico\'s voor alle weggebruikers. Parkeerovertreding dicht bij tramrails kan tot boetes en voertuigverwijdering leiden. (RVV 1990 art. 12)',
      ar: 'لا تقف أبدا على أو بالقرب من مسارات الترام. تتداخل المركبات المتوقفة مع تشغيل الترام وتخلق مخاطر أمان لجميع مستخدمي الطريق. قد تؤدي انتهاكات الوقوف بالقرب من مسارات الترام إلى غرامات وإزالة المركبة. (RVV 1990 art. 12)'
    },
    subject: 'Risk Awareness'
  },
  {
    id: 'q-ra-24',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/risk-awareness/hp-scene-1.jpg',
    text: {
      en: 'You are driving at 50 km/h on a priority road (voorrangsweg). A car at the give-way line on the right is partially hidden behind a hedge and inching forward. You have right of way — but what do you do?',
      nl: 'U rijdt met 50 km/h op een voorrangsweg. Een auto op de stopstreep aan de rechterkant is gedeeltelijk verborgen achter een haag en schuift langzaam naar voren. U hebt voorrang — maar wat doet u?',
      ar: 'أنت تقود بسرعة 50 كم/س على طريق ذي أولوية. سيارة عند خط الاستسلام على اليمين مختبأة جزئيًا خلف سياج وتتحرك ببطء للأمام. لديك حق الطريق — لكن ماذا تفعل؟'
    },
    options: [
      {
        id: 'q-ra-24o1',
        text: {
          en: 'Brake immediately',
          nl: 'Remt onmiddellijk',
          ar: 'اكبح الفرامل فورا'
        }
      },
      {
        id: 'q-ra-24o2',
        text: {
          en: 'Release accelerator',
          nl: 'Geef gas los',
          ar: 'حرر المسرع'
        }
      },
      {
        id: 'q-ra-24o3',
        text: {
          en: 'Do nothing',
          nl: 'Doet niets',
          ar: 'لا تفعل شيئا'
        }
      }
    ],
    correctAnswerId: 'q-ra-24o2',
    explanation: {
      en: 'You have priority, but that does not mean you can ignore the hazard. The hedge creates a blind spot — you cannot confirm the other driver has seen you or will yield. Release the accelerator to reduce speed and be ready to brake if the car pulls out. (RVV 1990 art. 45)',
      nl: 'U hebt voorrang, maar dat betekent niet dat u het gevaar kunt negeren. De haag creëert een dode hoek — u kunt niet bevestigen dat de andere bestuurder u heeft gezien of zal wijken. Geef gas los om snelheid te verminderen en wees bereid te remmen als de auto uittrekt. (RVV 1990 art. 45)',
      ar: 'لديك حق الطريق، لكن هذا لا يعني أنك تستطيع تجاهل الخطر. ينشئ السياج نقطة عمياء — لا يمكنك التأكد من أن السائق الآخر رآك أو سيفسح الطريق. حرر المسرع لتقليل السرعة وكن مستعدًا للكبح إذا خرجت السيارة. (RVV 1990 art. 45)'
    },
    subject: 'Risk Awareness'
  },
  {
    id: 'q-ra-25',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/risk-awareness/hp-scene-4.jpg',
    text: {
      en: 'You are driving at 50 km/h on a wet road. A cyclist is visibly unstable and drifting toward your lane. What is your response?',
      nl: 'U rijdt met 50 km/h op een natte weg. Een fietser is duidelijk instabiel en glijdt naar uw baan. Wat is uw reactie?',
      ar: 'أنت تقود بسرعة 50 كم/س على طريق مبللة. دراج غير مستقر بوضوح ويتجه نحو حارتك. ما ردك؟'
    },
    options: [
      {
        id: 'q-ra-25o1',
        text: {
          en: 'Brake immediately',
          nl: 'Remt onmiddellijk',
          ar: 'اكبح الفرامل فورا'
        }
      },
      {
        id: 'q-ra-25o2',
        text: {
          en: 'Release accelerator',
          nl: 'Geef gas los',
          ar: 'حرر المسرع'
        }
      },
      {
        id: 'q-ra-25o3',
        text: {
          en: 'Do nothing',
          nl: 'Doet niets',
          ar: 'لا تفعل شيئا'
        }
      }
    ],
    correctAnswerId: 'q-ra-25o2',
    explanation: {
      en: 'The cyclist is visibly unstable and drifting toward your lane on a wet road. Release the accelerator — this reduces speed without the skid risk of hard braking on a wet surface. Give the cyclist space and be ready to brake if they fully enter your lane. (RVV 1990 art. 10)',
      nl: 'De fietser is duidelijk instabiel en glijdt op een natte weg naar uw baan. Geef gas los — dit vermindert de snelheid zonder het sliprisico van hard remmen op een natte ondergrond. Geef de fietser ruimte en wees bereid te remmen als ze volledig in uw baan terechtkomen. (RVV 1990 art. 10)',
      ar: 'الدراج غير مستقر بوضوح ويتجه نحو حارتك على طريق مبللة. حرر المسرع — وهذا يقلل السرعة دون مخاطر الانزلاق من الكبح الشديد على سطح مبلل. أعط الدراج مساحة وكن مستعدًا للكبح إذا دخلوا بالكامل إلى حارتك. (RVV 1990 art. 10)'
    },
    subject: 'Risk Awareness'
  },
];
